import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { viewManagerState } from '~/state/atoms/engineAtoms';

export interface ViewportDragHandle {
    /** Re-renders on change; use for styling the dragged element. */
    readonly isDragging: boolean;
    /** The same state read synchronously, for event handlers that run
     *  before React has committed the re-render. */
    readonly isDraggingNow: () => boolean;
    /** Begins a drag from a mousedown. */
    readonly startDrag: (options: StartDragOptions) => void;
}

export interface StartDragOptions {
    /** CSS cursor held for the drag's duration, e.g. `ns-resize`. */
    readonly cursor: string;
    /** Called for every `mousemove` until mouseup. */
    readonly onMove: (event: MouseEvent) => void;
}

/**
 * The drag lifecycle a viewport overlay needs to own a pointer gesture:
 * engine tools suppressed and the drag cursor shown for its duration (both
 * through the viewer's ToolManager `beginCrosshairDrag`, which owns the
 * viewport cursor), `document`-level listeners so the pointer may leave the
 * element, and every one of those released on mouseup OR on an unmount
 * mid-gesture.
 *
 * The unmount path is the reason this is one hook rather than a pattern:
 * an overlay torn down between mousedown and mouseup would otherwise
 * leave the engine's tools suppressed and the viewport stuck on the drag
 * cursor, with no gesture left to end and release them.
 */
export function useViewportDragHandle(): ViewportDragHandle {
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;
    const [isDragging, setIsDragging] = useState(false);
    const isDraggingRef = useRef(false);
    const abortRef = useRef<AbortController | null>(null);

    const endDrag = useCallback(() => {
        toolManager?.endCrosshairDrag();
        isDraggingRef.current = false;
        setIsDragging(false);
        abortRef.current?.abort();
        abortRef.current = null;
    }, [toolManager]);

    // Releases everything a drag still in flight holds. `endDrag` sets
    // state, which React ignores on an unmounted component; the tool flag
    // and the cursor are the parts that outlive it and actually matter.
    // Owner-scoped: only the instance whose drag is in flight ends it.
    // With one overlay per view, an unrelated overlay's unmount must not
    // end another view's active drag; its own listeners are always dropped.
    useEffect(
        () => () => {
            if (isDraggingRef.current) {
                endDrag();
                return;
            }
            abortRef.current?.abort();
            abortRef.current = null;
        },
        [endDrag]
    );

    const startDrag = useCallback(
        ({ cursor, onMove }: StartDragOptions) => {
            toolManager?.beginCrosshairDrag(cursor);
            isDraggingRef.current = true;
            setIsDragging(true);

            const abortController = new AbortController();
            abortRef.current = abortController;
            document.addEventListener('mousemove', onMove, {
                signal: abortController.signal,
            });
            document.addEventListener('mouseup', endDrag, {
                signal: abortController.signal,
            });
        },
        [toolManager, endDrag]
    );

    const isDraggingNow = useCallback(() => isDraggingRef.current, []);

    return {
        isDragging,
        isDraggingNow,
        startDrag,
    };
}
